import {
  catchErrorInto,
  effect,
  Entity,
  exhaustMapSwitchUntilChanged,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
  smartRetry,
} from '@toeverything/infra';
import { map, tap } from 'rxjs';

import type { AuthService } from '../services/auth';
import type { ServerService } from '../services/server';
import type {
  ClaudeTokenStatusResponse,
  KeysStatusResponse,
  RoutingScenario,
  UserAiMode,
  UserApiKeysModeStore,
} from '../stores/user-api-keys-mode';

/**
 * Entity for managing User API Keys Mode state.
 *
 * Provides reactive state for:
 * - Current AI mode (standard/user-keys)
 * - Keys configuration status
 * - Claude token status
 */
export class UserApiKeysMode extends Entity {
  mode$ = new LiveData<UserAiMode | null>(null);

  openaiKeySet$ = new LiveData<boolean>(false);
  openaiKeyMasked$ = new LiveData<string | null>(null);
  webSearchKeySet$ = new LiveData<boolean>(false);
  webSearchKeyMasked$ = new LiveData<string | null>(null);
  claudeTokenSet$ = new LiveData<boolean>(false);
  claudeTokenExpiresAt$ = new LiveData<string | null>(null);
  claudeTokenIsExpired$ = new LiveData<boolean>(false);

  routingMatrix$ = new LiveData<RoutingScenario[]>([]);

  isRevalidating$ = new LiveData(false);
  isSaving$ = new LiveData(false);
  error$ = new LiveData<any | null>(null);

  constructor(
    private readonly authService: AuthService,
    private readonly store: UserApiKeysModeStore,
    private readonly serverService: ServerService
  ) {
    super();
  }

  revalidate = effect(
    map(() => ({
      accountId: this.authService.session.account$.value?.id,
    })),
    exhaustMapSwitchUntilChanged(
      (a, b) => a.accountId === b.accountId,
      ({ accountId }) =>
        fromPromise(async signal => {
          if (!accountId) {
            return null;
          }

          const serverConfig =
            await this.serverService.server.features$.waitForNonNull(signal);

          if (!serverConfig.copilot) {
            return null;
          }

          const [modeRes, keysRes, tokenRes, matrixRes] = await Promise.all([
            this.store.fetchMode(signal),
            this.store.fetchKeysStatus(signal),
            this.store.fetchClaudeTokenStatus(signal),
            this.store.fetchRoutingMatrix(signal),
          ]);

          return { modeRes, keysRes, tokenRes, matrixRes };
        }).pipe(
          smartRetry(),
          tap(data => {
            if (data) {
              this.applyData(
                data.modeRes,
                data.keysRes,
                data.tokenRes,
                data.matrixRes.scenarios
              );
            } else {
              this.reset();
            }
          }),
          catchErrorInto(this.error$),
          onStart(() => this.isRevalidating$.next(true)),
          onComplete(() => this.isRevalidating$.next(false))
        ),
      () => {
        this.reset();
      }
    )
  );

  private applyData(
    modeRes: { mode: UserAiMode },
    keysRes: KeysStatusResponse,
    tokenRes: ClaudeTokenStatusResponse,
    routingMatrix: RoutingScenario[]
  ) {
    this.mode$.next(modeRes.mode);
    this.openaiKeySet$.next(keysRes.openaiKeySet);
    this.openaiKeyMasked$.next(keysRes.openaiKeyMasked ?? null);
    this.webSearchKeySet$.next(keysRes.webSearchKeySet);
    this.webSearchKeyMasked$.next(keysRes.webSearchKeyMasked ?? null);
    this.claudeTokenSet$.next(keysRes.claudeTokenSet);
    this.claudeTokenExpiresAt$.next(keysRes.claudeTokenExpiresAt);
    this.claudeTokenIsExpired$.next(tokenRes.isExpired);
    this.routingMatrix$.next(routingMatrix);
  }

  async setMode(mode: UserAiMode): Promise<void> {
    this.isSaving$.next(true);
    this.error$.next(null);

    try {
      const result = await this.store.setMode(mode);
      this.mode$.next(result.mode);
      await this.refreshRoutingMatrix();
    } catch (error) {
      this.error$.next(error);
      throw error;
    } finally {
      this.isSaving$.next(false);
    }
  }

  async setOpenAiKey(key: string): Promise<void> {
    this.isSaving$.next(true);
    this.error$.next(null);

    try {
      await this.store.setOpenAiKey(key);
      this.openaiKeySet$.next(true);
      // Will be updated on next revalidate
      await this.refreshRoutingMatrix();
    } catch (error) {
      this.error$.next(error);
      throw error;
    } finally {
      this.isSaving$.next(false);
    }
  }

  async deleteOpenAiKey(): Promise<void> {
    this.isSaving$.next(true);
    this.error$.next(null);

    try {
      await this.store.deleteOpenAiKey();
      this.openaiKeySet$.next(false);
      this.openaiKeyMasked$.next(null);
      await this.refreshRoutingMatrix();
    } catch (error) {
      this.error$.next(error);
      throw error;
    } finally {
      this.isSaving$.next(false);
    }
  }

  async setWebSearchKey(key: string): Promise<void> {
    this.isSaving$.next(true);
    this.error$.next(null);

    try {
      await this.store.setWebSearchKey(key);
      this.webSearchKeySet$.next(true);
      // Will be updated on next revalidate
      await this.refreshRoutingMatrix();
    } catch (error) {
      this.error$.next(error);
      throw error;
    } finally {
      this.isSaving$.next(false);
    }
  }

  async deleteWebSearchKey(): Promise<void> {
    this.isSaving$.next(true);
    this.error$.next(null);

    try {
      await this.store.deleteWebSearchKey();
      this.webSearchKeySet$.next(false);
      this.webSearchKeyMasked$.next(null);
      await this.refreshRoutingMatrix();
    } catch (error) {
      this.error$.next(error);
      throw error;
    } finally {
      this.isSaving$.next(false);
    }
  }

  async refreshRoutingMatrix(): Promise<void> {
    try {
      const result = await this.store.fetchRoutingMatrix();
      this.routingMatrix$.next(result.scenarios);
    } catch (error) {
      console.error('Failed to refresh routing matrix:', error);
    }
  }

  canSwitchToUserKeys(): boolean {
    return this.claudeTokenSet$.value && !this.claudeTokenIsExpired$.value;
  }

  async initOAuth(): Promise<{ authUrl: string; state: string }> {
    this.isSaving$.next(true);
    this.error$.next(null);

    try {
      const result = await this.store.initOAuth();
      return result;
    } catch (error) {
      this.error$.next(error);
      throw error;
    } finally {
      this.isSaving$.next(false);
    }
  }

  async exchangeOAuthCode(
    code: string,
    state: string
  ): Promise<{ success: boolean; expiresAt: string }> {
    this.isSaving$.next(true);
    this.error$.next(null);

    try {
      const result = await this.store.exchangeOAuthCode(code, state);

      this.claudeTokenSet$.next(true);
      this.claudeTokenExpiresAt$.next(result.expiresAt);
      this.claudeTokenIsExpired$.next(false);

      await this.refreshRoutingMatrix();

      return result;
    } catch (error) {
      this.error$.next(error);
      throw error;
    } finally {
      this.isSaving$.next(false);
    }
  }

  reset() {
    this.mode$.next(null);
    this.openaiKeySet$.next(false);
    this.openaiKeyMasked$.next(null);
    this.webSearchKeySet$.next(false);
    this.webSearchKeyMasked$.next(null);
    this.claudeTokenSet$.next(false);
    this.claudeTokenExpiresAt$.next(null);
    this.claudeTokenIsExpired$.next(false);
    this.routingMatrix$.next([]);
    this.error$.next(null);
    this.isRevalidating$.next(false);
    this.isSaving$.next(false);
  }

  override dispose(): void {
    this.revalidate.unsubscribe();
  }
}
